#import "src/queries/fragments/personCardFragment.graphql"

fragment TeamMembersFields on Group {
  id
  name
  kind
  type
  dailyRate @include(if: $includeOverlayTeamDailyRate)
  isHidden
  isDeleted
  isRootForSource
  enableSupplyStructureEdits
  supplyStructureRoot {
    id
    name
    isRootForSource
    enableSupplyStructureEdits
  }
  directParentId
  hierarchyIds # needed for move group modal
  memberCount
  directMemberCount
  childTeamCount
  directChildTeamCount
  supplyOwners @include(if: $includeOwners) {
    aggregateId
    firstName
    surname
    preferredName
    avatar
    jobTitle
    fte
    # Tags for reporting-line owner cards when owner ∉ team members
    tags: appliedTags {
      id
      tagId
      type
      displayValue
      isActive
      attributes
      entityTagAttributes
    }
    memberships(input: { membershipTypes: [OWNER] }) {
      currentCompositeId
      groupId
      ownerRole
    }
    supplyOwnerOf {
      id
      name
      type
      hierarchy
      hierarchyIds
      parentIds
    }
    memberOf {
      id
      name
      type
      hierarchy
      hierarchyIds
      parentIds
    }
  }
  supplyInferredOwnedBy {
    ...PersonCardFragment
  }
  attributes {
    id
    attributeType
    subtype
    label
    value
  }
  permissions {
    updateGroupAttributes
    canMutateGroup
  }
  tags: appliedTags {
    id
    tagId
    type
    displayValue
    isActive
    attributes
    tag {
      tagAttributesMetaData {
        person {
          aggregateId
          preferredName
          firstName
          surname
          avatar
        }
      }
    }
  }
  members(
    limit: $membersLimit
    offset: $membersOffset
    excludeHidden: $membersExcludeHidden
    excludeDeleted: $membersExcludeDeleted
  ) @include(if: $includeMembers) {
    customerEmployeeId
    aggregateId
    employeeId
    firstName
    surname
    preferredName
    jobTitle
    fte
    avatar
    country @include(if: $includeOverlayPersonFields)
    city @include(if: $includeOverlayPersonFields)
    state @include(if: $includeOverlayPersonFields)
    address @include(if: $includeOverlayPersonFields)
    company @include(if: $includeOverlayPersonFields)
    timezone @include(if: $includeOverlayPersonFields)
    bands @include(if: $includeOverlayPersonFields)
    directWorkforce @include(if: $includeOverlayPersonFields)
    attributes {
      attributeType
      subtype
      value
    }
    allAttributes @include(if: $includeOverlayAllAttributes) {
      attributeType
      subtype
      value
    }
    isPlaceholder
    isVacancy
    disabled
    # Live seats this person fills — drives the org-chart position line on PersonNode.
    filledPositions @include(if: $includePositionFields) {
      id
      occupancyStatus
      person {
        aggregateId
        preferredName
        firstName
        surname
      }
    }
    tags: appliedTags {
      id
      tagId
      type
      displayValue
      isActive
    }
    allocations {
      fte
      targetGroupId
    }
    memberOf {
      id
      name
      type
      hierarchy
      hierarchyIds
      parentIds
    }
    supplyOwnerOf {
      id
      name
      type
      hierarchy
      hierarchyIds
      parentIds
    }
    memberships(input: { membershipTypes: null }) {
      groupId
      type
      ownerRole
    }
    managing {
      aggregateId
      fte
    }
    supplyManaging {
      aggregateId
      fte
    }
    managedBy {
      aggregateId
      firstName
      preferredName
      surname
      avatar
      jobTitle
    }
    # Names required: hasPersonReportsTo → isValidPerson rejects aggregateId-only
    # stubs as UNKNOWN_NAME, which made Org Chart details show "Reports to: None".
    supplyManagedBy {
      aggregateId
      firstName
      preferredName
      surname
      avatar
      jobTitle
    }
    # TPP-1543 stand-in for supplyManagedBy when it's a gap (null) — always
    # requested, the API nulls it itself when enableDemoODInferredOwners is off.
    supplyInferredManagedBy {
      ...PersonCardFragment
    }
  }
}
fragment ChildTeamListFields on Group {
  id
  name
  kind
  type
  dailyRate @include(if: $includeOverlayTeamDailyRate)
  isHidden
  isDeleted
  isRootForSource
  enableSupplyStructureEdits
  supplyStructureRoot {
    id
    name
    isRootForSource
    enableSupplyStructureEdits
  }
  directParentId
  hierarchyIds # needed for move group modal
  memberCount
  childTeamCount
  directMemberCount
  directChildTeamCount
  supplyOwners @include(if: $includeOwners) {
    aggregateId
    firstName
    surname
    preferredName
    avatar
    jobTitle
    fte
    # Tags for reporting-line owner cards when owner ∉ team members
    tags: appliedTags {
      id
      tagId
      type
      displayValue
      isActive
      attributes
      entityTagAttributes
    }
    memberships(input: { membershipTypes: [OWNER] }) {
      currentCompositeId
      groupId
      ownerRole
    }
    supplyOwnerOf {
      id
      name
      type
      hierarchy
      hierarchyIds
      parentIds
    }
    memberOf {
      id
      name
      type
      hierarchy
      hierarchyIds
      parentIds
    }
  }
  supplyInferredOwnedBy {
    ...PersonCardFragment
  }
  attributes {
    id
    attributeType
    subtype
    label
    value
  }
  tags: appliedTags {
    id
    tagId
    type
    displayValue
    isActive
    attributes
    tag {
      tagAttributesMetaData {
        person {
          aggregateId
          preferredName
          firstName
          surname
          avatar
        }
      }
    }
  }
  permissions {
    updateGroupAttributes
    canMutateGroup
    canMutateMemberships
  }
}

query getTeamAndMembersAndChildTeamsQuery(
  $groupId: String!
  $includeMembers: Boolean = true
  $membersLimit: Int = 500
  $membersOffset: Int = 0
  $membersExcludeHidden: Boolean = true
  $membersExcludeDeleted: Boolean = true
  $includeTeams: Boolean = true
  $teamsLimit: Int = 500
  $teamsOffset: Int = 0
  $includeOwners: Boolean = false
  $includePositionFields: Boolean = false
  $includeOverlayTeamDailyRate: Boolean = false
  $includeOverlayAllAttributes: Boolean = false
  $includeOverlayPersonFields: Boolean = false
) {
  team: getGroup(groupId: $groupId) {
    ...TeamMembersFields
    childTeams(limit: $teamsLimit, offset: $teamsOffset)
      @include(if: $includeTeams) {
      ...ChildTeamListFields
    }
  }
}

query getTeamAndMembersAndChildTeamsWithOptionQuery(
  $groupId: String!
  $includeMembers: Boolean = true
  $membersLimit: Int = 500
  $membersOffset: Int = 0
  $membersExcludeHidden: Boolean = true
  $membersExcludeDeleted: Boolean = true
  $includeTeams: Boolean = true
  $teamsLimit: Int = 500
  $teamsOffset: Int = 0
  $includeOwners: Boolean = false
  $includePositionFields: Boolean = false
  $includeOverlayTeamDailyRate: Boolean = false
  $includeOverlayAllAttributes: Boolean = false
  $includeOverlayPersonFields: Boolean = false
  $optionId: ID
) {
  team: getGroupWithOption(groupId: $groupId, optionId: $optionId) {
    ...TeamMembersFields
    childTeams(limit: $teamsLimit, offset: $teamsOffset)
      @include(if: $includeTeams) {
      ...ChildTeamListFields
    }
  }
}
